import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent } from '@/components/ui';
import { Button, Input, Label, Spinner } from '@/components/ui';
import { PasswordRequirements, isPasswordValid } from '@/components/features/auth/password-requirements';

/**
 * Passwort-Reset-Seite — Zwei-Schritt-Flow.
 * Schritt 1: Email eingeben → Code anfordern
 * Schritt 2: Code + neues Passwort eingeben → Reset abschliessen
 *
 * @see Spec 0043 Section 4.2
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { resetPassword, confirmResetPassword } = useAuth();

  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleRequestCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Bitte geben Sie Ihre E-Mail-Adresse ein.');
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword(email.trim());
      // Generische Meldung — auch bei ungueltigem Email (Spec 0043: kein User-Leaking)
      setStep(2);
    } catch (err) {
      // Auch bei Fehlern generische Meldung (kein Leaking)
      if (err instanceof Error && (err.name === 'LimitExceededException' || err.name === 'TooManyRequestsException')) {
        setError('Zu viele Anfragen. Bitte warten Sie einen Moment.');
      } else {
        // Trotzdem zu Step 2 wechseln — generische Meldung
        setStep(2);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError('Bitte geben Sie den Bestätigungscode ein.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    if (!isPasswordValid(newPassword)) {
      setError('Das Passwort erfüllt nicht alle Anforderungen.');
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmResetPassword(email.trim(), code.trim(), newPassword);
      navigate('/login?reset=success', { replace: true });
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'CodeMismatchException') {
          setError('Der Bestätigungscode ist ungültig.');
        } else if (err.name === 'ExpiredCodeException') {
          setError('Der Code ist abgelaufen. Bitte fordern Sie einen neuen an.');
        } else if (err.name === 'LimitExceededException' || err.name === 'TooManyRequestsException') {
          setError('Zu viele Versuche. Bitte warten Sie einen Moment.');
        } else if (err.name === 'InvalidPasswordException') {
          setError('Das Passwort erfüllt nicht die Anforderungen.');
        } else {
          setError('Passwort-Reset fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardContent className="pt-8 pb-6">
          <h1 className="mb-6 text-center text-lg font-semibold">
            {step === 1 ? 'Passwort zurücksetzen' : 'Neues Passwort festlegen'}
          </h1>

          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 p-3 text-center text-sm text-destructive">
              {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleRequestCode} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Geben Sie Ihre E-Mail-Adresse ein. Falls ein Konto existiert, erhalten Sie einen Bestätigungscode.
              </p>

              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2"><Spinner size="sm" />Senden…</span>
                ) : (
                  'Code anfordern'
                )}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                  Zurück zum Login
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="rounded-md bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950 dark:text-green-300">
                Falls ein Konto mit dieser E-Mail existiert, wurde ein Bestätigungscode gesendet.
              </div>

              <p className="text-sm text-muted-foreground">
                Geben Sie den Code aus der E-Mail ein und wählen Sie ein neues Passwort.
              </p>

              <div className="space-y-2">
                <Label htmlFor="code">Bestätigungscode</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  placeholder="123456"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">Neues Passwort</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? 'Verbergen' : 'Anzeigen'}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>

              <PasswordRequirements password={newPassword} />

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2"><Spinner size="sm" />Zurücksetzen…</span>
                ) : (
                  'Passwort zurücksetzen'
                )}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground hover:underline">
                  Zurück zum Login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
